import { FontAwesome5 } from '@expo/vector-icons'
import { RadioButton } from '@pulseops/common'
import React, { useState } from 'react'
import { useTranslation } from 'react-i18next'
import { ActivityIndicator, StyleProp, StyleSheet, Text, TouchableOpacity, View, ViewStyle } from 'react-native'

interface Props {
  title?: string | JSX.Element
  titleOutline?: string
  allowToggle?: boolean
  children?: React.ReactNode
  containerStyle?: StyleProp<ViewStyle>
  isLoading?: boolean
  isNoPadding?: boolean
  isExpandedToggle?: boolean
  isNewLA?: boolean
  LAIndex?: number
  isViewDetail?: boolean
  removeItem?: (index: number) => void
  viewDetailEvent?: (index: number) => void
  isDelete?: boolean
  setIsDelete?: (status: boolean, index: number) => void
  hasShowedBenefitsRegistration?: boolean
  isCheckedBenefitsRegistration?: boolean
  isNewLAForBenefitsRegistration?: boolean
  onCheckBenefitsRegistrationEvent?: (value: boolean, index: number) => void
}

export const RiderPanel: React.FC<Props> = ({
  title,
  isLoading = false,
  children,
  titleOutline,
  allowToggle,
  containerStyle,
  isNoPadding = false,
  isExpandedToggle = false,
  isNewLA = false,
  LAIndex,
  isViewDetail = false,
  viewDetailEvent,
  setIsDelete,
  onCheckBenefitsRegistrationEvent,
  hasShowedBenefitsRegistration,
  isCheckedBenefitsRegistration,
  isNewLAForBenefitsRegistration
}) => {
  const [isOpenedToggle, setIsOpenedToggle] = useState(false)
  const { t } = useTranslation()
  const onToggle = () => allowToggle !== false && setIsOpenedToggle(!isOpenedToggle)
  const showActions = !hasShowedBenefitsRegistration || isNewLAForBenefitsRegistration

  React.useMemo(() => { setIsOpenedToggle(isExpandedToggle) }, [isExpandedToggle])

  const renderTitle = () => {
    if (!title) return null
    if (!isNewLA) return <Text style={styles.sectionInfoHeaderTitle}>{title}</Text>
    return (
      <View style={styles.titleRow}>
        <Text style={[styles.sectionInfoHeaderTitle, styles.marginText]}>{title}</Text>
        <Text style={[styles.newText, styles.btnActions]}>{`(${t('RiderAlteration:New')})`}</Text>
        {showActions && (
          <>
            <TouchableOpacity onPress={() => viewDetailEvent?.(LAIndex ?? -1)}>
              <Text style={[styles.detailText, styles.btnActions]}>{t('RiderAlteration:Details')}</Text>
            </TouchableOpacity>
            {!isViewDetail && (
              <TouchableOpacity onPress={() => setIsDelete?.(true, LAIndex ?? -1)}>
                <Text style={styles.detailText}>{t('common:Delete')}</Text>
              </TouchableOpacity>
            )}
          </>
        )}
      </View>
    )
  }

  return (
    <View>
      {!!titleOutline && <Text style={[styles.sectionInfoHeaderTitle, styles.titleOutline]}>{titleOutline}</Text>}
      <View style={[styles.sectionInfo, containerStyle, isNoPadding && styles.noPadding]}>
        <View style={[styles.sectionInfoHeader, !title && styles.sectionInfoHeaderInnerRight, isNoPadding && styles.touchableBtn, !isOpenedToggle && styles.bottomPadding]}>
          <TouchableOpacity onPress={onToggle} style={styles.transactionContent}>
            <View style={styles.titleRow}>
              {hasShowedBenefitsRegistration && (
                <RadioButton
                  onChange={() => onCheckBenefitsRegistrationEvent?.(!isCheckedBenefitsRegistration, LAIndex ?? -1)}
                  selected={isCheckedBenefitsRegistration}
                  disable={isViewDetail}
                  style={styles.radioButton}
                />
              )}
              {renderTitle()}
            </View>
            <FontAwesome5 name={isOpenedToggle ? 'minus-circle' : 'plus-circle'} color="#1B365D" size={25} />
          </TouchableOpacity>
        </View>
        {isOpenedToggle && <View style={styles.sectionInfoContent}>{isLoading ? <ActivityIndicator size="large" color="#ED1B2E" /> : children}</View>}
      </View>
    </View>
  )
}

const styles = StyleSheet.create({
  titleOutline: { marginBottom: 10 },
  sectionInfo: { width: '100%', padding: 30, backgroundColor: '#FFF', borderWidth: 1, borderColor: '#D3DCE6', borderRadius: 8 },
  sectionInfoHeader: { flexDirection: 'row', justifyContent: 'space-between', alignContent: 'center' },
  sectionInfoHeaderInnerRight: { justifyContent: 'flex-end' },
  sectionInfoHeaderTitle: { fontSize: 15, lineHeight: 22, fontWeight: '800', textTransform: 'uppercase' },
  sectionInfoContent: { marginTop: 16 },
  noPadding: { padding: 0, backgroundColor: '#FAFAFA' },
  transactionContent: { flex: 1, flexDirection: 'row', justifyContent: 'space-between', alignContent: 'center' },
  touchableBtn: { paddingTop: 20, paddingLeft: 20, paddingRight: 20 },
  bottomPadding: { paddingBottom: 20 },
  titleRow: { flex: 1, flexDirection: 'row' },
  marginText: { marginRight: 8 },
  btnActions: { marginRight: 16 },
  newText: { fontStyle: 'italic', fontWeight: '400', fontSize: 15, lineHeight: 22, color: '#ED1B2E' },
  detailText: { fontWeight: '400', fontSize: 15, lineHeight: 22, color: '#1EA5FC', textDecorationLine: 'underline' },
  radioButton: { marginRight: 15 }
})
